import React from "react";
import { ImageFitOptions, TopBottom } from ".";
import {
  ComponentProps,
  ComponentWidth,
  Image,
  KontentRichText,
  PageLink,
  Video,
  LeftRight,
  Vector,
} from "../../../types";
import { SectionWrapper } from "../../containers/SectionWrapper";
import { Cta } from "../../elements/Cta/Cta";
import { CustomRichTextElement } from "../../elements/CustomRichTextElement/CustomRichTextElement";
import { Equals } from "../../elements/Equals/equals";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";
import { Link } from "../../elements/Link/Link";
import VideoPlayer from "../../elements/VideoPlayer/VideoPlayer";
import * as styles from "./FeaturedMedia.module.css";

interface Props extends ComponentProps, ComponentWidth {
  accentBottomRightCorner: boolean;
  cornerRounding: TopBottom;
  cta?: PageLink;
  featuredMedia: Image | Video;
  featuredMediaPlacement: LeftRight;
  imageFit?: ImageFitOptions;
  heading: string;
  secondaryCta?: PageLink;
  text?: KontentRichText;
  vector?: Vector;
  vectorPlacement?: "top" | "bottom";
}

export const FeaturedMedia: React.FC<Props> = ({
  featuredMedia,
  featuredMediaPlacement,
  heading,
  text,
  cta,
  secondaryCta,
  vector,
  vectorPlacement,
  cornerRounding,
  accentBottomRightCorner,
  width,
  imageFit,
  ...props
}) => {
  const wrapperClasses = [styles.featuredimage];

  if (featuredMediaPlacement === "right") {
    wrapperClasses.push(styles.imageRight);
  }

  if (width === "contained") {
    wrapperClasses.push(styles.contained);
  }

  const renderMedia = (media: any) => {
    if (media?.videoUrl) {
      return (
        <VideoPlayer
          className={styles.featured}
          src={media?.videoUrl}
          thumbnail={media?.thumbnail?.item?.url}
          display="inline"
          arrowSize="88px"
        />
      );
    }

    return (
      <KontentImageElement
        aspectRatio={imageFit !== "contain" ? 811 / 580 : undefined}
        objectFit={imageFit || "cover"}
        width={1500}
        height={1500 * (580 / 811)}
        layout="fullWidth"
        image={media?.item}
        alt={media?.alt}
        className={styles.featured}
        animation="fadeInUp"
      />
    );
  };

  const imageClasses = [styles.image];
  cornerRounding === "bottom" && imageClasses.push(styles.roundBottom);
  cornerRounding === "top" && imageClasses.push(styles.roundTop);

  const vectorImageStyle =
    vectorPlacement === "bottom" ? styles.vectorBottom : styles.vector;
  return (
    <SectionWrapper {...props}>
      <div className={wrapperClasses.join(" ")}>
        <div
          className={imageClasses.join(" ")}
          data-kontent-element-codename="featured_media"
        >
          {vector?.item?.url && vector?.item?.type === "image/svg+xml" && (
            <div
              className={vectorImageStyle}
              data-kontent-element-codename="vector"
            >
              <KontentImageElement
                aspectRatio={1}
                objectFit="contain"
                layout="constrained"
                image={vector?.item}
                alt={vector?.alt}
              />
            </div>
          )}
          {renderMedia(featuredMedia)}
        </div>
        <div className={styles.contentContainer}>
          <div className={styles.content} data-kontent-element-codename="text">
            <h2 data-kontent-element-codename="heading">{heading}</h2>
            {text && <CustomRichTextElement content={text} />}
            {cta && (
              <div
                className={styles.ctaContainer}
                data-kontent-element-codename="link_configuration__link"
              >
                <Cta href={cta?.href} label={cta?.label} target={cta?.target} />
                {secondaryCta && (
                  <div className={styles.secondaryCta}>
                    <Link to={secondaryCta.href} target={secondaryCta.target}>
                      {secondaryCta.label}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {accentBottomRightCorner && (
          <Equals className={styles.accent} id={`featuredImage${props.id}`} />
        )}
      </div>
    </SectionWrapper>
  );
};
